import React from 'react';
import { Link } from 'react-router-dom';

const PrivacyPolicy: React.FC = () => {
  return (
    <div className="bg-white">

      {/* Hero Header */}
      <section className="relative overflow-hidden bg-neutral-50">
        <div className="section-container py-16 sm:py-20 lg:py-28">
          <nav className="flex items-center space-x-2 text-sm text-neutral-400 mb-10">
            <Link to="/" className="hover:text-forest-600 transition-colors">Home</Link>
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
            </svg>
            <span className="text-neutral-950 font-medium">Privacy Policy</span>
          </nav>
          <h1 className="font-display text-display-sm sm:text-display-md lg:text-display-lg text-neutral-950 tracking-tight mb-4">
            Privacy Policy
          </h1>
          <p className="text-body-lg text-neutral-500 max-w-2xl">
            Last updated: March 1, 2026. Your privacy is fundamental to everything we do. We collect only what is necessary and protect it with care.
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="section-container py-16 lg:py-24">
        <div className="max-w-3xl mx-auto">

          {/* Introduction */}
          <div className="mb-14">
            <div className="bg-forest-50/50 border border-forest-100 rounded-2xl p-6 sm:p-8">
              <h2 className="font-display text-heading text-neutral-950 tracking-tight mb-3">Our Commitment to Privacy</h2>
              <p className="text-body text-neutral-600 leading-relaxed">
                LiberaCure ("Company," "we," "us," or "our") is committed to a privacy-first approach. We believe you should have complete control over your personal data. This Privacy Policy explains what information we collect, how we use it, and what rights you have in relation to it. We operate on the principle of data minimization — we collect only the information strictly necessary to provide our services and fulfill your orders.
              </p>
            </div>
          </div>

          {/* 1. Data Collection */}
          <div className="mb-14">
            <h2 className="font-display text-heading-lg text-neutral-950 tracking-tight mb-4">1. Information We Collect</h2>
            <div className="space-y-4 text-body text-neutral-600 leading-relaxed">
              <p>
                We collect information in three ways: information you provide directly, information collected automatically when you use our Services, and information from third-party sources.
              </p>

              <h3 className="font-display text-heading text-neutral-950 mt-6 mb-2">1.1 Information You Provide</h3>
              <ul className="list-disc list-outside ml-6 space-y-2">
                <li><strong className="text-neutral-800">Account Information:</strong> Name, email address, and password when you create an account</li>
                <li><strong className="text-neutral-800">Order Information:</strong> Shipping address, phone number, and billing details necessary to fulfill your order</li>
                <li><strong className="text-neutral-800">Communication Data:</strong> Any information you provide when contacting customer support, including email correspondence and chat messages</li>
              </ul>

              <h3 className="font-display text-heading text-neutral-950 mt-6 mb-2">1.2 Information Collected Automatically</h3>
              <ul className="list-disc list-outside ml-6 space-y-2">
                <li><strong className="text-neutral-800">Device Information:</strong> Browser type, operating system, device type, and screen resolution</li>
                <li><strong className="text-neutral-800">Usage Data:</strong> Pages visited, time spent on pages, click patterns, and referring URLs</li>
                <li><strong className="text-neutral-800">Network Data:</strong> IP address (anonymized after 30 days), general geographic location (country/region level only)</li>
              </ul>

              <h3 className="font-display text-heading text-neutral-950 mt-6 mb-2">1.3 What We Do Not Collect</h3>
              <p>
                We do not collect health records, medical history, diagnostic information, or any data related to your specific medical conditions. We do not purchase or obtain personal data from data brokers. We do not collect biometric data, social security numbers, government-issued identification numbers, or financial information beyond what is necessary for payment processing.
              </p>
            </div>
          </div>

          {/* 2. How We Use Your Information */}
          <div className="mb-14">
            <h2 className="font-display text-heading-lg text-neutral-950 tracking-tight mb-4">2. How We Use Your Information</h2>
            <div className="space-y-4 text-body text-neutral-600 leading-relaxed">
              <p>We use the information we collect for the following purposes:</p>
              <ul className="list-disc list-outside ml-6 space-y-2">
                <li><strong className="text-neutral-800">Order Fulfillment:</strong> Processing, shipping, and delivering your orders; sending order confirmations and shipping updates</li>
                <li><strong className="text-neutral-800">Account Management:</strong> Creating and managing your account, authenticating your identity, and providing customer support</li>
                <li><strong className="text-neutral-800">Service Improvement:</strong> Analyzing aggregate usage patterns to improve website functionality, product offerings, and user experience</li>
                <li><strong className="text-neutral-800">Security:</strong> Detecting and preventing fraud, unauthorized access, and other harmful activities</li>
                <li><strong className="text-neutral-800">Legal Compliance:</strong> Complying with applicable laws, regulations, and legal processes</li>
                <li><strong className="text-neutral-800">Communications:</strong> Responding to your inquiries and, only with your explicit consent, sending promotional materials or product updates. You may opt out at any time</li>
              </ul>
              <p>
                We will never sell your personal information to third parties. We will never use your data for purposes beyond those described in this policy without obtaining your explicit consent.
              </p>
            </div>
          </div>

          {/* 3. Cookies */}
          <div className="mb-14">
            <h2 className="font-display text-heading-lg text-neutral-950 tracking-tight mb-4">3. Cookies and Tracking Technologies</h2>
            <div className="space-y-4 text-body text-neutral-600 leading-relaxed">
              <p>
                We use a minimal set of cookies that are strictly necessary for the operation of our Services. We do not use third-party advertising cookies or cross-site tracking technologies.
              </p>

              <div className="overflow-x-auto mt-4">
                <table className="w-full text-sm border border-neutral-150 rounded-2xl overflow-hidden">
                  <thead>
                    <tr className="bg-neutral-50">
                      <th className="text-left px-4 py-3 font-semibold text-neutral-950 border-b border-neutral-150">Cookie Type</th>
                      <th className="text-left px-4 py-3 font-semibold text-neutral-950 border-b border-neutral-150">Purpose</th>
                      <th className="text-left px-4 py-3 font-semibold text-neutral-950 border-b border-neutral-150">Duration</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-100">
                    <tr>
                      <td className="px-4 py-3 text-neutral-800">Essential</td>
                      <td className="px-4 py-3 text-neutral-600">Session management, authentication, shopping cart</td>
                      <td className="px-4 py-3 text-neutral-600">Session / 30 days</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-neutral-800">Preference</td>
                      <td className="px-4 py-3 text-neutral-600">Language, currency, display preferences</td>
                      <td className="px-4 py-3 text-neutral-600">1 year</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-neutral-800">Analytics</td>
                      <td className="px-4 py-3 text-neutral-600">Anonymous, aggregated usage statistics (no personal identification)</td>
                      <td className="px-4 py-3 text-neutral-600">90 days</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <p>
                You can control cookie preferences through your browser settings. Disabling essential cookies may affect the functionality of our Services, including the ability to place orders.
              </p>
            </div>
          </div>

          {/* 4. Third Parties */}
          <div className="mb-14">
            <h2 className="font-display text-heading-lg text-neutral-950 tracking-tight mb-4">4. Third-Party Service Providers</h2>
            <div className="space-y-4 text-body text-neutral-600 leading-relaxed">
              <p>
                We share your information with a limited number of trusted third-party service providers who assist us in operating our business. These providers are contractually bound to use your information only for the specific services they provide to us and are required to maintain appropriate security measures.
              </p>
              <ul className="list-disc list-outside ml-6 space-y-2">
                <li><strong className="text-neutral-800">Payment Processors:</strong> Receive payment details necessary to process transactions. We do not store full credit card numbers on our servers</li>
                <li><strong className="text-neutral-800">Shipping Carriers:</strong> Receive name, address, and phone number necessary for delivery</li>
                <li><strong className="text-neutral-800">Hosting Providers:</strong> Store data on secure, encrypted servers</li>
              </ul>
              <p>
                We do not share your personal information with advertisers, data brokers, or any other third parties for marketing purposes. We may disclose your information if required by law, court order, or governmental authority, or if we believe disclosure is necessary to protect the rights, property, or safety of LiberaCure, our customers, or others.
              </p>
            </div>
          </div>

          {/* 5. Data Retention */}
          <div className="mb-14">
            <h2 className="font-display text-heading-lg text-neutral-950 tracking-tight mb-4">5. Data Retention</h2>
            <div className="space-y-4 text-body text-neutral-600 leading-relaxed">
              <p>
                We retain your personal information only for as long as necessary to fulfill the purposes for which it was collected, including to satisfy legal, accounting, or reporting requirements.
              </p>
              <div className="bg-neutral-50 rounded-2xl border border-neutral-150 p-6 mt-4">
                <h3 className="font-display text-heading text-neutral-950 mb-3">Retention Schedule</h3>
                <ul className="space-y-2">
                  <li className="flex justify-between py-1 border-b border-neutral-100 last:border-0">
                    <span className="text-neutral-600">Account data</span>
                    <span className="text-neutral-950 font-medium">Until account deletion</span>
                  </li>
                  <li className="flex justify-between py-1 border-b border-neutral-100 last:border-0">
                    <span className="text-neutral-600">Order records</span>
                    <span className="text-neutral-950 font-medium">5 years (legal requirement)</span>
                  </li>
                  <li className="flex justify-between py-1 border-b border-neutral-100 last:border-0">
                    <span className="text-neutral-600">Support correspondence</span>
                    <span className="text-neutral-950 font-medium">2 years after resolution</span>
                  </li>
                  <li className="flex justify-between py-1 border-b border-neutral-100 last:border-0">
                    <span className="text-neutral-600">Analytics data</span>
                    <span className="text-neutral-950 font-medium">90 days (anonymized)</span>
                  </li>
                  <li className="flex justify-between py-1">
                    <span className="text-neutral-600">IP addresses</span>
                    <span className="text-neutral-950 font-medium">30 days (then anonymized)</span>
                  </li>
                </ul>
              </div>
              <p>
                When personal data is no longer required, it is securely deleted or anonymized using industry-standard methods that prevent recovery.
              </p>
            </div>
          </div>

          {/* 6. Your Rights */}
          <div className="mb-14">
            <h2 className="font-display text-heading-lg text-neutral-950 tracking-tight mb-4">6. Your Rights</h2>
            <div className="space-y-4 text-body text-neutral-600 leading-relaxed">
              <p>
                Depending on your jurisdiction, you may have the following rights regarding your personal information. We honor these rights for all customers regardless of location:
              </p>
              <ul className="list-disc list-outside ml-6 space-y-2">
                <li><strong className="text-neutral-800">Right to Access:</strong> Request a copy of the personal data we hold about you</li>
                <li><strong className="text-neutral-800">Right to Rectification:</strong> Request correction of inaccurate or incomplete personal data</li>
                <li><strong className="text-neutral-800">Right to Erasure:</strong> Request deletion of your personal data, subject to legal retention obligations</li>
                <li><strong className="text-neutral-800">Right to Restrict Processing:</strong> Request that we limit how we use your data</li>
                <li><strong className="text-neutral-800">Right to Data Portability:</strong> Request your data in a structured, commonly used, and machine-readable format</li>
                <li><strong className="text-neutral-800">Right to Object:</strong> Object to processing of your personal data for certain purposes, including direct marketing</li>
                <li><strong className="text-neutral-800">Right to Withdraw Consent:</strong> Where processing is based on consent, you may withdraw consent at any time without affecting the lawfulness of prior processing</li>
              </ul>
              <p>
                To exercise any of these rights, please contact us at <strong className="text-neutral-800">support@liberacure.com</strong>. We will respond to your request within 30 days. We may request verification of your identity before processing your request.
              </p>
            </div>
          </div>

          {/* 7. Security */}
          <div className="mb-14">
            <h2 className="font-display text-heading-lg text-neutral-950 tracking-tight mb-4">7. Data Security</h2>
            <div className="space-y-4 text-body text-neutral-600 leading-relaxed">
              <p>
                We implement and maintain robust technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. These measures include:
              </p>
              <ul className="list-disc list-outside ml-6 space-y-2">
                <li>TLS/SSL encryption for all data transmitted between your browser and our servers</li>
                <li>AES-256 encryption for personal data stored at rest</li>
                <li>Regular security audits and vulnerability assessments</li>
                <li>Strict access controls with role-based permissions for employees</li>
                <li>Secure, encrypted backups with limited retention periods</li>
              </ul>
              <p>
                While we take every reasonable precaution to protect your data, no method of transmission over the Internet or electronic storage is 100% secure. We cannot guarantee absolute security but commit to promptly notifying affected users in the event of a data breach.
              </p>
            </div>
          </div>

          {/* 8. International Transfers */}
          <div className="mb-14">
            <h2 className="font-display text-heading-lg text-neutral-950 tracking-tight mb-4">8. International Data Transfers</h2>
            <div className="space-y-4 text-body text-neutral-600 leading-relaxed">
              <p>
                As an international service, your personal information may be transferred to and processed in countries other than the country in which you reside. These countries may have data protection laws that are different from the laws of your country.
              </p>
              <p>
                When we transfer your data internationally, we ensure appropriate safeguards are in place, including but not limited to standard contractual clauses, encryption in transit and at rest, and contractual obligations on our service providers to maintain equivalent levels of data protection.
              </p>
              <p>
                By using our Services and providing your personal information, you consent to the transfer of your data to countries outside your country of residence, subject to the safeguards described above.
              </p>
            </div>
          </div>

          {/* 9. Children's Privacy */}
          <div className="mb-14">
            <h2 className="font-display text-heading-lg text-neutral-950 tracking-tight mb-4">9. Children's Privacy</h2>
            <div className="space-y-4 text-body text-neutral-600 leading-relaxed">
              <p>
                Our Services are not directed to individuals under the age of 18. We do not knowingly collect personal information from children. If we become aware that we have inadvertently collected personal data from a child, we will take steps to delete such information as soon as possible. If you believe a child has provided us with personal information, please contact us immediately.
              </p>
            </div>
          </div>

          {/* 10. Changes */}
          <div className="mb-14">
            <h2 className="font-display text-heading-lg text-neutral-950 tracking-tight mb-4">10. Changes to This Policy</h2>
            <div className="space-y-4 text-body text-neutral-600 leading-relaxed">
              <p>
                We may update this Privacy Policy from time to time to reflect changes in our practices, technologies, legal requirements, or other factors. When we make material changes, we will update the "Last updated" date at the top of this page and notify you via email or a prominent notice on our website at least 14 days before the changes take effect.
              </p>
              <p>
                We encourage you to review this Privacy Policy periodically to stay informed about how we protect your information.
              </p>
            </div>
          </div>

          {/* 11. Contact */}
          <div className="mb-14">
            <h2 className="font-display text-heading-lg text-neutral-950 tracking-tight mb-4">11. Contact Us</h2>
            <div className="space-y-4 text-body text-neutral-600 leading-relaxed">
              <p>
                If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact our Data Protection team:
              </p>
              <div className="bg-neutral-50 rounded-2xl border border-neutral-150 p-6">
                <p className="font-medium text-neutral-950 mb-1">LiberaCure Data Protection</p>
                <p>Email: privacy@liberacure.com</p>
                <p>General Support: support@liberacure.com</p>
                <p>Response time: Within 24 hours on business days</p>
              </div>
              <p>
                If you are not satisfied with our response or believe we are processing your personal data in violation of applicable law, you have the right to lodge a complaint with the relevant data protection authority in your jurisdiction.
              </p>
            </div>
          </div>

          {/* Effective Date */}
          <div className="bg-neutral-50 rounded-2xl border border-neutral-150 p-6 text-center">
            <p className="text-body text-neutral-500">
              This Privacy Policy is effective as of March 1, 2026.
            </p>
          </div>

        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;
